import { CircularProgress, Stack } from "@mui/material";
import type { FC } from "react";
import { Navigate, useParams } from "react-router-dom";
import { useCalculatorAccessQuery } from "web_myplann/Api/Calculators/useCalculatorAccessQuery";
import { CalculatorPageLayout } from "web_myplann/Calculators/Components/CalculatorPageLayout";
import { AmericanMortgageCalculatorPage } from "web_myplann/Calculators/Pages/AmericanMortgageCalculatorPage";
import { ChildrenSavingsCalculatorPage } from "web_myplann/Calculators/Pages/ChildrenSavingsCalculatorPage";
import { ConsumerLoanCalculatorPage } from "web_myplann/Calculators/Pages/ConsumerLoanCalculatorPage";
import { GoalAchieveCalculatorPage } from "web_myplann/Calculators/Pages/GoalAchieveCalculatorPage";
import { MortgageCalculatorPage } from "web_myplann/Calculators/Pages/MortgageCalculatorPage";
import { PensionAnnuityCalculatorPage } from "web_myplann/Calculators/Pages/PensionAnnuityCalculatorPage";
import { RefinancingCalculatorPage } from "web_myplann/Calculators/Pages/RefinancingCalculatorPage";
import { CalculatorType } from "web_myplann/Calculators/Types/CalculatorType";
import type { InsuranceCalculatorType } from "web_myplann/Calculators/Types/InsuranceCalculatorType";
import { CALCULATOR_TYPES } from "web_myplann/Calculators/Utils/calculatorCatalog";
import { isInsuranceCalculator } from "web_myplann/Calculators/Utils/isInsuranceCalculator";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";

const CALCULATOR_PAGES: Record<
	Exclude<CalculatorType, InsuranceCalculatorType>,
	FC
> = {
	[CalculatorType.Mortgage]: MortgageCalculatorPage,
	[CalculatorType.Refinancing]: RefinancingCalculatorPage,
	[CalculatorType.AmericanMortgage]: AmericanMortgageCalculatorPage,
	[CalculatorType.ConsumerLoan]: ConsumerLoanCalculatorPage,
	[CalculatorType.PensionAnnuity]: PensionAnnuityCalculatorPage,
	[CalculatorType.ChildrenSavings]: ChildrenSavingsCalculatorPage,
	[CalculatorType.GoalAchieve]: GoalAchieveCalculatorPage,
};

export const CalculatorDetailPage = () => {
	const { type } = useParams<{ type: string }>();
	const calculatorType = CALCULATOR_TYPES.find((item) => item === type);
	const { data: access, isPending, isPaused } = useCalculatorAccessQuery();

	if (!calculatorType || isInsuranceCalculator(calculatorType)) {
		return <Navigate to={Routing.Calculators.CalculatorList.path()} replace />;
	}

	if (!access && isPending && !isPaused) {
		return (
			<CalculatorPageLayout type={calculatorType}>
				<Stack minHeight="100%" alignItems="center" justifyContent="center">
					<CircularProgress />
				</Stack>
			</CalculatorPageLayout>
		);
	}

	if (
		!access?.[calculatorType].isVisible ||
		!access[calculatorType].hasAccess
	) {
		return <Navigate to={Routing.Calculators.CalculatorList.path()} replace />;
	}

	const CalculatorPage = CALCULATOR_PAGES[calculatorType];

	return <CalculatorPage />;
};
